// SPDX-License-Identifier: Apache-2.0
import GeoGirafeAppComponent from './geogirafe-component';
import tippy from 'tippy.js';
import AboutComponent from '../../components/about/component';
import BasemapComponent from '../../components/basemap/component';
import CoordinateComponent from '../../components/coordinate/component';
import ContactComponent from '../../components/contact/component';
import MapDefaultContextMenuComponent from '../../components/context-menu/default-context-menu/component';
import MapCustomContextMenuComponent from '../../components/context-menu/custom-context-menu/component';
import CrossSectionSettingsComponent from '../../components/cross-section/cross-section-settings/component';
import CrossSectionViewComponent from '../../components/cross-section/cross-section-viewer/component';
import DrawingComponent, { DrawingStateLocation } from '../../components/drawing/component';
import EditComponent from '../../components/edit/component';
import EditFromComponent from '../../components/edit/editform/component';
import HelpComponent from '../../components/help/component';
import InfoboxComponent from '../../components/infobox/component';
import InfoWindowComponent from '../../components/infowindow/component';
import LayoutComponent from '../../components/layout/component';
import LRPanelComponent from '../../components/lr-panel/component';
import Map2dComponent from '../../components/map-2d/component';
import DeprecatedMapComponent from '../../components/map-2d/deprecatedmapcomponent';
import Map3dComponent from '../../components/map-3d/component';
import MapGridComponent from '../../components/map-grid/component';
import MenuButtonComponent from '../../components/menubutton/component';
import ModalsComponent from '../../components/modals/component';
import NavigationComponent from '../../components/navigation/component';
import NewsButtonComponent from '../../components/news/news-button/component';
import NewsPanelComponent from '../../components/news/news-panel/component';
import OauthComponent from '../../components/auth/component';
import PrintComponent from '../../components/print/component';
import PrototypeBannerComponent from '../../components/prototypebanner/component';
import QueryBuilderComponent from '../../components/querybuilder/component';
import ScaleComponent from '../../components/scale/component';
import SearchComponent from '../../components/search/component';
import SelectionGridComponent from '../../components/selectiongrid/component';
import SelectionWindowComponent from '../../components/selectionwindow/component';
import ShareComponent from '../../components/share/component';
import ExternalLayersComponent from '../../components/extlayers/component';
import ThemeComponent from '../../components/themes/component';
import TimeRestrictionComponent from '../../components/timerestriction/component';
import TimePickerComponent from '../../components/timerestriction/timepicker/component';
import TimeSliderComponent from '../../components/timerestriction/timeslider/component';
import TreeViewGroupComponent from '../../components/treeview/treeviewgroup/component';
import TreeViewItemComponent from '../../components/treeview/treeviewitem/component';
import TreeViewRootComponent from '../../components/treeview/treeviewroot/component';
import TreeViewThemeComponent from '../../components/treeview/treeviewtheme/component';
import UserPreferencesComponent from '../../components/userpreferences/component';
import VideoRecordComponent from '../../components/videorecord/component';
import DrawingState from '../drawing/drawingState';
import DrawingSerializer from '../drawing/drawingSerializer';
import { ShareState, ShareStateSerializer } from '../../components/share/sharestate';
import SelectionToolComponent from '../../components/selectiontool/component';
import AdvancedFilterComponent from '../../components/advancedfilter/component';
import FixedDimensionComponent from '../../components/drawing/fixed-dimension/component';
import GetDirectionsArtifact from '../../components/getdirections/component';
import ServiceWorkerHelper from '../utils/swhelper';
import FeedbackButtonComponent from '../../components/feedbackbutton/component';
import AddBookmarkComponent from '../../components/addbookmark/component';
import DrawingToolbarComponent from '../../components/drawing-toolbar/component';
import SearchFilterComponent from '../../components/search-filter/component';
import HeaderComponent from '../../components/header/component';

export default class GeoGirafeApp {
  private readonly readyPromise: Promise<void>;
  private resolveReady!: (value: void | PromiseLike<void>) => void;
  protected mainComponent!: GeoGirafeAppComponent;
  private readonly isIframe: boolean;

  public get context() {
    return this.mainComponent.getContext();
  }

  public constructor(isIframe: boolean = false) {
    this.isIframe = isIframe;
    this.readyPromise = new Promise((resolve) => {
      this.resolveReady = resolve;
    });

    this.initialize();
  }

  public async isReady(): Promise<void> {
    return this.readyPromise;
  }

  protected async initialize() {
    this.defineMainComponent();
    await this.mainComponent.isReady();
    await this.initializeServiceWorker();
    this.initializeInterface();
    this.addCustomSerializers();
    this.context.stateManager.state.application.isCustomSerializerInitialized = true;
    this.defineCoreComponents();
    this.resolveReady();
  }

  protected defineMainComponent() {
    // Define custom element for the main application
    customElements.define('girafe-app', GeoGirafeAppComponent);
    this.mainComponent = document.querySelector('girafe-app') as GeoGirafeAppComponent;
    if (!this.mainComponent) {
      throw new Error('Application GeoGirafe not found. Please verify that the component is present in the HTML.');
    }
  }

  protected initializeInterface() {
    // Override default tooltip maxWidth:
    tippy.setDefaultProps({ maxWidth: '' });
  }

  protected addCustomSerializers() {
    // Add custom state and serializers (need to be done early, because the shared state will need them)
    this.context.stateManager.state.extendedState[DrawingStateLocation] = new DrawingState();
    this.context.stateSerializer.addSerializer(DrawingState, new DrawingSerializer(this.context));
    this.context.stateManager.state.extendedState.share = new ShareState();
    this.context.stateSerializer.addSerializer(ShareState, new ShareStateSerializer(this.context, this.isIframe));
  }

  protected defineCoreComponents() {
    customElements.define('girafe-about', AboutComponent);
    customElements.define('girafe-advanced-filter', AdvancedFilterComponent);
    customElements.define('girafe-header', HeaderComponent);
    customElements.define('girafe-basemap', BasemapComponent);
    customElements.define('girafe-coordinate', CoordinateComponent);
    customElements.define('girafe-contact', ContactComponent);
    customElements.define('girafe-default-context-menu', MapDefaultContextMenuComponent);
    customElements.define('girafe-custom-context-menu', MapCustomContextMenuComponent);
    customElements.define('girafe-layout', LayoutComponent);
    customElements.define('girafe-help', HelpComponent);
    customElements.define('girafe-infobox', InfoboxComponent);
    customElements.define('girafe-info-window', InfoWindowComponent);
    customElements.define('girafe-cross-section-settings', CrossSectionSettingsComponent);
    customElements.define('girafe-cross-section-view', CrossSectionViewComponent);
    customElements.define('girafe-lr-panel', LRPanelComponent);
    customElements.define('girafe-map-2d', Map2dComponent);
    customElements.define('girafe-map-3d', Map3dComponent);
    customElements.define('girafe-map-grid', MapGridComponent);
    // Deprecated alias of <girafe-map-2d>, kept for the applications that were not migrated yet.
    customElements.define('girafe-map', DeprecatedMapComponent);
    customElements.define('girafe-menu-button', MenuButtonComponent);
    customElements.define('girafe-modals', ModalsComponent);
    customElements.define('girafe-nav-history', NavigationComponent);
    customElements.define('girafe-news-button', NewsButtonComponent);
    customElements.define('girafe-news', NewsPanelComponent);
    customElements.define('girafe-print', PrintComponent);
    customElements.define('girafe-prototype-banner', PrototypeBannerComponent);
    customElements.define('girafe-query-builder', QueryBuilderComponent);
    customElements.define('girafe-oauth', OauthComponent);
    customElements.define('girafe-drawing', DrawingComponent);
    customElements.define('girafe-drawing-toolbar', DrawingToolbarComponent);
    customElements.define('girafe-edit', EditComponent);
    customElements.define('girafe-edit-form', EditFromComponent);
    customElements.define('girafe-scale', ScaleComponent);
    customElements.define('girafe-search-filter', SearchFilterComponent);
    customElements.define('girafe-search', SearchComponent);
    customElements.define('girafe-selection-tool', SelectionToolComponent);
    customElements.define('girafe-selection-grid', SelectionGridComponent);
    customElements.define('girafe-selection-window', SelectionWindowComponent);
    customElements.define('girafe-share', ShareComponent);
    customElements.define('girafe-ext-layer', ExternalLayersComponent);
    customElements.define('girafe-theme-select', ThemeComponent);
    customElements.define('girafe-time-restriction', TimeRestrictionComponent);
    customElements.define('girafe-time-picker', TimePickerComponent);
    customElements.define('girafe-time-slider', TimeSliderComponent);
    customElements.define('girafe-tree-view', TreeViewRootComponent);
    customElements.define('girafe-tree-view-group', TreeViewGroupComponent);
    customElements.define('girafe-tree-view-item', TreeViewItemComponent);
    customElements.define('girafe-tree-view-theme', TreeViewThemeComponent);
    customElements.define('girafe-user-preferences', UserPreferencesComponent);
    customElements.define('girafe-video-record', VideoRecordComponent);
    customElements.define('girafe-fixed-dimension', FixedDimensionComponent);
    customElements.define('girafe-get-directions', GetDirectionsArtifact);
    customElements.define('girafe-feedback-button', FeedbackButtonComponent);
    customElements.define('girafe-add-bookmark', AddBookmarkComponent);
  }

  private async initializeServiceWorker() {
    try {
      const storeVersion: number = 6;
      const dbCacheName: string = 'geogirafe-cache';
      if (globalThis.location.protocol !== 'https:') {
        throw new Error('Service worker registration not possible if the application is served over HTTP.');
      }
      if (!navigator?.serviceWorker) {
        throw new Error('Service worker not supported by your browser.');
      }

      let sw: ServiceWorker;
      try {
        sw = await this.waitForServiceWorkerControl();
      } catch (err) {
        throw new Error(`Service worker registration failed. ${err}`, { cause: err });
      }

      const config = this.context.configManager.Config;
      const serviceWorkerHelper = new ServiceWorkerHelper(sw);

      // Communicate configuration to service-worker
      // NOTE REG : Important : we have to wait that the config has been correctly sent to the SW
      // Otherwise the first requests can be sent without the correct auhentication.
      const issuerConfig = config.oauth?.issuer ?? config.gmfauth;
      const gmfConfig = config.oauth?.geomapfish ?? config.gmfauth;
      if (issuerConfig && gmfConfig) {
        const issuerHostname = new URL(issuerConfig.url).hostname;
        const audience = [...issuerConfig.audience, issuerHostname];
        const swConfig = {
          logLevel: config.general.logLevel,
          audience: audience,
          audienceExcludedPaths: issuerConfig.audienceExcludedPaths,
          authMode: gmfConfig.authMode,
          alwaysSendCookies: issuerConfig.alwaysSendCookies,
          refererPolicy: gmfConfig.refererPolicy
        };
        await serviceWorkerHelper.sendMessageToServiceWorker(swConfig);
      } else {
        // If no auth, we just update the log level, but we do not have to wait
        sw.postMessage({ logLevel: config.general.logLevel });
      }
      await this.context.offlineManager.setServiceWorker(sw, storeVersion, dbCacheName);
      await this.context.authManager.initialize(sw);
    } catch (e) {
      console.error("Service worker could not be initialized. Authentication and offline maps won't work.\n", e);
      // Finish auth initialization, since without a service worker auth isn't possible
      this.context.stateManager.state.application.isAuthInitialized = true;
    }
  }

  private async waitForServiceWorkerControl(): Promise<ServiceWorker> {
    const sw = navigator.serviceWorker;
    const registration = await sw.register('service-worker.js', { type: 'module' });

    // If a service worker already controls the current page, everything is ready.
    // `registration.active` only tells us that a service worker is active for the registration;
    // `sw.controller` tells us that it actually controls this page.
    if (sw.controller) {
      return sw.controller;
    }

    // The page is not currently controlled by a service worker.
    // This can happen, for example, after a force refresh (Ctrl+F5), even though
    // an active service worker already exists. In that case, the service worker's
    // `activate` event is not triggered again, so the `clients.claim()` call from
    // the activation phase cannot take control of this newly loaded page.
    // If an active service worker already exists, explicitly ask it to claim the
    // current page. The service worker handles this message by calling `clients.claim()`.
    const active = registration.active;
    if (active) {
      active.postMessage({ type: 'claim' });
    }

    // Wait until the service worker takes control of the current page.
    // Once `clients.claim()` succeeds, the browser updates `sw.controller` and
    // dispatches the `controllerchange` event.
    return new Promise((resolve) => {
      sw.addEventListener(
        'controllerchange',
        () => {
          const controller = navigator.serviceWorker.controller;
          if (controller) {
            resolve(controller);
          }
        },
        { once: true }
      );
    });
  }
}
